import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import BWModal from 'components/shared/BWModal/BWModal';
import BWAccordion from 'components/shared/BWAccordion';
import FormItem from 'components/shared/BWFormControl/FormItem';
import FormInput from 'components/shared/BWFormControl/FormInput';
import FormSelect from 'components/shared/BWFormControl/FormSelect';
import FormTextArea from 'components/shared/BWFormControl/FormTextArea';

export const WARRANTY_BILLING_OPTIONS = [{ value: 'WARRANTY', label: 'Miễn phí' }, { value: 'PAID', label: 'Có phí' }];
const blank = { type_code: '', type_name: '', applicability: '', billing_type: '', warranty_period_id: null, workflow_id: null, covered_scope: '', description: '', is_active: true };
const requiredText = (label, max) => ({
  validate: value => Boolean(String(value ?? '').trim()) || `Nhập ${label.toLowerCase()}.`,
  maxLength: { value: max, message: `${label} tối đa ${max} ký tự.` },
});

export default function WarrantyTypeForm({ item, periods, workflows = [], readOnly, busy, onSave, onClose }) {
  const methods = useForm({ defaultValues: item || blank });
  const disabled = readOnly || busy;
  const submit = methods.handleSubmit(onSave);
  const workflowId = methods.watch('workflow_id');
  const workflow = workflows.find(row => Number(row.workflow_id) === Number(workflowId));
  const workflowOptions = workflows.map(row => ({ value: row.workflow_id, label: `${row.code} — ${row.name}` }));
  if (item?.workflow_id && !workflowOptions.some(row => Number(row.value) === Number(item.workflow_id))) {
    workflowOptions.push({ value: item.workflow_id, label: `${item.workflow_name || item.workflow_code} (ngừng sử dụng)`, disabled: true });
  }
  return <BWModal open size='lg' title={readOnly ? 'Chi tiết hình thức bảo hành' : item ? 'Chỉnh sửa hình thức bảo hành' : 'Thêm hình thức bảo hành'}
    modalClassName='bw_warranty_type_modal' onClose={onClose} loading={busy} maskClosable={false}
    dirty={methods.formState.isDirty} confirmBeforeClose cancelAction={{ label: 'Đóng' }}
    primaryAction={readOnly ? undefined : { label: 'Lưu hình thức', type: 'success', icon: 'fi fi-rr-check', onClick: submit, loading: busy }}>
    <FormProvider {...methods}>
      <form onSubmit={submit}>
        <BWAccordion title='Thông tin chung' hideCollapse>
          <div className='bw_row'>
            <FormItem className='bw_col_4' label='Mã hình thức' isRequired disabled={disabled}>
              <FormInput field='type_code' placeholder='Nhập mã hình thức' maxLength={50} validation={{
                required: 'Nhập mã hình thức.',
                pattern: { value: /^[A-Za-z0-9][A-Za-z0-9_-]{0,49}$/, message: 'Mã gồm chữ không dấu, số, dấu gạch ngang hoặc gạch dưới; tối đa 50 ký tự.' },
              }} />
            </FormItem>
            <FormItem className='bw_col_8' label='Tên hình thức' isRequired disabled={disabled}>
              <FormInput field='type_name' placeholder='Nhập tên hình thức bảo hành' maxLength={250} validation={requiredText('Tên hình thức', 250)} />
            </FormItem>
            <FormItem className='bw_col_12' label='Đối tượng áp dụng' isRequired disabled={disabled}>
              <FormTextArea field='applicability' rows={3} placeholder='Nhập đối tượng và điều kiện áp dụng' validation={requiredText('Đối tượng áp dụng', 2000)} />
            </FormItem>
            <FormItem className='bw_col_6' label='Thu phí' isRequired disabled={disabled}>
              <FormSelect field='billing_type' list={WARRANTY_BILLING_OPTIONS} placeholder='Chọn hình thức thu phí' allowClear={false} validation={{ required: 'Chọn hình thức thu phí.' }} />
            </FormItem>
            <div className='bw_col_6 bw_warranty_type_active'>
              <label className='bw_checkbox'><FormInput type='checkbox' field='is_active' disabled={disabled} />
                <span /> Đang sử dụng
              </label>
            </div>
          </div>
        </BWAccordion>
        <BWAccordion title='Quy trình xử lý' hideCollapse>
          <FormItem label='Quy trình áp dụng' isRequired disabled={disabled} helperText='Phiếu mới tự áp dụng quy trình này ngay khi tạo.'>
            <FormSelect field='workflow_id' aria-label='Quy trình áp dụng' list={workflowOptions} placeholder='Chọn quy trình đã khai báo' allowClear
              validation={{ validate: value => !methods.getValues('is_active') || Boolean(value) || 'Chọn quy trình áp dụng cho hình thức bảo hành.' }} />
          </FormItem>
          {(workflow?.steps || (readOnly ? item?.workflow_steps : []))?.length > 0 && <ol className='bw_warranty_type_steps'>
            {(workflow?.steps || item.workflow_steps).map(step => <li key={step.code}>{step.name}</li>)}
          </ol>}
          {!workflows.length && !readOnly && <p>Chưa có quy trình đang sử dụng. Khai báo tại Danh mục khai báo → Quy trình bảo hành.</p>}
        </BWAccordion>
        <BWAccordion title='Bảo hành sau sửa' hideCollapse>
          <div className='bw_row'>
            <FormItem className='bw_col_6' label='Thời hạn bảo hành sau sửa' disabled={disabled} helperText='Để trống nếu thời hạn được chọn trên từng phiếu.'>
              <FormSelect field='warranty_period_id' list={periods} placeholder='Chọn trên từng phiếu' allowClear />
            </FormItem>
            <FormItem className='bw_col_12' label='Hạng mục/phạm vi bảo hành sau sửa' isRequired disabled={disabled}>
              <FormTextArea field='covered_scope' rows={3} placeholder='Nhập hạng mục, linh kiện và phạm vi được bảo hành sau sửa' validation={requiredText('Hạng mục/phạm vi bảo hành', 2000)} />
            </FormItem>
          </div>
        </BWAccordion>
        <BWAccordion title='Ghi chú' hideCollapse>
          <FormItem label='Ghi chú' disabled={disabled}>
            <FormTextArea field='description' rows={2} placeholder='Nhập ghi chú' validation={{ maxLength: { value: 2000, message: 'Ghi chú tối đa 2000 ký tự.' } }} />
          </FormItem>
        </BWAccordion>
        <p className='bw_warranty_type_note'>Thay đổi chỉ áp dụng cho lần chọn mới. Chính sách đã lưu trên phiếu tiếp nhận được giữ nguyên.</p>
      </form>
    </FormProvider>
  </BWModal>;
}
